import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  clearPersistedSession,
  clearSessionIfExpired,
  loadPersistedSession,
  persistSession,
} from '@/auth/authSession';
import { buildLoginBody } from '@/auth/core/buildLoginBody';
import { isBearerExpired } from '@/auth/core/isBearerExpired';
import { postLogin } from '@/auth/core/postLogin';
import { postLogout } from '@/auth/core/postLogout';
import { validateLoginForm } from '@/auth/core/validateLoginForm';
import {
  consumeSessionExpiredNotice,
  SESSION_EXPIRED_MESSAGE,
} from '@/auth/sessionNotice';
import type { AuthSession, LoginError } from '@/auth/types';

interface AuthContextValue {
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  loginNotice: string | null;
  login: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: LoginError }>;
  clearLoginNotice: () => void;
  expireSession: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides auth session state backed by coolture_auth.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => loadPersistedSession());
  const [loginNotice, setLoginNotice] = useState<string | null>(() =>
    consumeSessionExpiredNotice() ? SESSION_EXPIRED_MESSAGE : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const applySession = useCallback((next: AuthSession | null) => {
    setSession(next);
    if (next) {
      persistSession(next);
    } else {
      clearPersistedSession();
    }
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const validation = validateLoginForm(username, password);
      if (!validation.valid) {
        return {
          success: false,
          error: {
            code: 'validation' as const,
            message: validation.missingFields
              ? 'Email and password are required.'
              : 'Enter a valid email address.',
          },
        };
      }

      setIsLoading(true);

      try {
        const body = buildLoginBody(username, password);
        const result = await postLogin(body);

        if (!result.ok) {
          return { success: false, error: result.error };
        }

        if (result.mfaRequired) {
          return {
            success: false,
            error: {
              code: 'server' as const,
              message: 'Multi-factor authentication is required but not supported in this console yet.',
            },
          };
        }

        applySession(result.session);
        return { success: true };
      } catch (error) {
        const message =
          error instanceof Error && error.message.includes('VITE_PRIVATE_KEY')
            ? 'Login is not configured (missing VITE_PRIVATE_KEY).'
            : 'Unable to sign in. Please try again.';
        return {
          success: false,
          error: { code: 'misconfigured' as const, message },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [applySession]
  );

  const clearLoginNotice = useCallback(() => {
    setLoginNotice(null);
  }, []);

  const expireSession = useCallback(() => {
    applySession(null);
    setLoginNotice(SESSION_EXPIRED_MESSAGE);
  }, [applySession]);

  useEffect(() => {
    if (session != null && isBearerExpired(session.bearerToken)) {
      expireSession();
    }
  }, [expireSession, session]);

  useEffect(() => {
    const validateOnFocus = () => {
      if (clearSessionIfExpired(session)) {
        expireSession();
      }
    };

    window.addEventListener('focus', validateOnFocus);
    return () => window.removeEventListener('focus', validateOnFocus);
  }, [expireSession, session]);

  const logout = useCallback(async () => {
    const currentSession = session;
    setIsLoggingOut(true);

    try {
      if (currentSession) {
        await postLogout(currentSession);
      }
    } finally {
      applySession(null);
      setLoginNotice(null);
      setIsLoggingOut(false);
    }
  }, [applySession, session]);

  const value = useMemo<AuthContextValue>(() => {
    const sessionIsValid = session !== null && !isBearerExpired(session.bearerToken);

    return {
      session,
      isAuthenticated: sessionIsValid,
      isLoading,
      isLoggingOut,
      loginNotice,
      login,
      clearLoginNotice,
      expireSession,
      logout,
    };
  }, [
    session,
    isLoading,
    isLoggingOut,
    loginNotice,
    login,
    clearLoginNotice,
    expireSession,
    logout,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Access auth context (must be used within AuthProvider).
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
